import { Fragment } from "react";
import {
  PRE_SUBMIT_STEP_DEFS,
  getPreSubmitStepperState,
  type JourneySectionId,
} from "../../../../utils/donationRequestUiStages";

type Props = {
  landLoaded: boolean;
  activeSection: JourneySectionId;
  hasRequestRow: boolean;
  isDraft: boolean;
  requirementsMet: boolean;
  onSelectSection: (id: JourneySectionId) => void;
};

function StepIcon({ stepKey, className }: { stepKey: string; className?: string }) {
  const cn = className ?? "h-5 w-5";
  switch (stepKey) {
    case "land":
      return (
        <svg className={cn} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
      );
    case "applicant":
      return (
        <svg className={cn} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
      );
    case "documents":
      return (
        <svg className={cn} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      );
    case "send":
      return (
        <svg
          className={`${cn} -scale-x-100`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
        </svg>
      );
    default:
      return null;
  }
}

export default function LandDonationJourneyStepper({
  landLoaded,
  activeSection,
  hasRequestRow,
  isDraft,
  requirementsMet,
  onSelectSection,
}: Props) {
  const { completed, activeIndex } = getPreSubmitStepperState({
    landLoaded,
    activeSection,
    hasRequestRow,
    isDraft,
    requirementsMet,
  });

  const renderCircle = (i: number, stepKey: string, isDone: boolean, isActive: boolean) => (
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all duration-300 ease-out ${
        isDone
          ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/25"
          : isActive
            ? "bg-sa-600 text-white shadow-lg shadow-sa-600/30 ring-2 ring-sa-200 ring-offset-2 ring-offset-white"
            : "border-2 border-neutral-200 bg-white text-neutral-400"
      } ${isActive && !isDone ? "scale-105" : ""}`}
      aria-hidden
    >
      {isDone ? (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.25} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      ) : (
        <StepIcon stepKey={stepKey} className={isActive ? "h-5 w-5 text-white" : "h-5 w-5 text-neutral-500"} />
      )}
    </span>
  );

  return (
    <div
      className="rounded-2xl border border-neutral-200/80 bg-gradient-to-br from-white to-neutral-50/90 p-4 shadow-md shadow-neutral-200/40 backdrop-blur-sm md:p-5"
      dir="rtl"
    >
      {/* Mobile: vertical rail — صف واحد = زر واحد */}
      <div className="space-y-0 md:hidden">
        {PRE_SUBMIT_STEP_DEFS.map((step, i) => {
          const isDone = completed[i];
          const isActive = i === activeIndex;
          return (
            <button
              key={step.key}
              type="button"
              onClick={() => onSelectSection(step.section)}
              className={`mb-3 flex w-full gap-3 rounded-2xl border px-2 py-2 text-right transition-all duration-300 ease-out last:mb-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-sa-500 ${
                isActive
                  ? "border-sa-200/90 bg-sa-50/90 shadow-md shadow-sa-900/5"
                  : "border-transparent bg-transparent hover:bg-white/90"
              }`}
            >
              <div className="flex flex-col items-center">
                {renderCircle(i, step.key, isDone, isActive)}
                {i < PRE_SUBMIT_STEP_DEFS.length - 1 && (
                  <div
                    className={`my-1 min-h-[28px] w-0.5 flex-1 rounded-full transition-colors duration-500 ${
                      completed[i] ? "bg-gradient-to-b from-emerald-500 to-teal-400" : "bg-neutral-200"
                    }`}
                    aria-hidden
                  />
                )}
              </div>
              <span
                className={`flex min-h-[3rem] flex-1 items-center text-sm font-semibold leading-snug ${
                  isActive ? "text-sa-900" : isDone ? "text-neutral-800" : "text-neutral-500"
                }`}
              >
                {step.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* md+: horizontal with connectors */}
      <div className="hidden md:flex md:w-full md:items-stretch md:justify-between md:gap-1">
        {PRE_SUBMIT_STEP_DEFS.map((step, i) => {
          const isDone = completed[i];
          const isActive = i === activeIndex;
          return (
            <Fragment key={step.key}>
              {i > 0 && (
                <div
                  className="mx-0 flex min-w-[24px] max-w-[48px] flex-1 items-center self-center"
                  aria-hidden
                >
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-200/90">
                    <div
                      className={`h-full rounded-full bg-gradient-to-l from-emerald-500 to-teal-400 transition-all duration-500 ease-out ${
                        completed[i - 1] ? "w-full opacity-100" : "w-0 opacity-40"
                      }`}
                    />
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => onSelectSection(step.section)}
                className={`group flex min-w-0 max-w-[200px] flex-1 flex-col items-center gap-2 rounded-2xl px-2 py-3 transition-all duration-300 ease-out hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-sa-500 ${
                  isActive ? "bg-sa-50/90 ring-1 ring-sa-200/80 shadow-sm" : "hover:bg-white/80"
                }`}
              >
                {renderCircle(i, step.key, isDone, isActive)}
                <span
                  className={`line-clamp-2 w-full text-center text-xs font-semibold leading-snug transition-colors duration-300 ${
                    isActive ? "text-sa-900" : isDone ? "text-neutral-800" : "text-neutral-500"
                  }`}
                >
                  {step.label}
                </span>
              </button>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
